import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Trophy, LayoutDashboard, CalendarCheck, Users, PhoneCall, RefreshCw, Search, Download,
  TrendingUp, Clock, Zap, ShieldCheck, ChevronDown, ChevronUp, CheckCircle, XCircle,
  Loader2, Send, Menu, ArrowRight, ArrowLeft, User, Phone, Mail, FileText, MessageSquare,
  Activity, Database, Server, Wifi, AlertTriangle, Ban, Trash2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { generateReceipt } from '../utils/receipt';
import { TurfDatePicker } from '../components/TurfDatePicker';
import { TurfTimePicker } from '../components/TurfTimePicker';
import { ISTClock } from '../components/ISTClock';
import { SEO } from '../components/SEO';
import { CalendarSyncButton } from '../components/CalendarSyncButton';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { format, addDays } from 'date-fns';
import { API_URL } from '../utils/api';

const ALL_SLOTS = [
  '03:00','04:00','05:00','06:00','07:00','08:00','09:00','10:00','11:00','12:00',
  '13:00','14:00','15:00','16:00','17:00','18:00','19:00','20:00','21:00','22:00','23:00','00:00','01:00','02:00'
];
const HAPPY_SET = new Set(['03:00','04:00','05:00','06:00','07:00','08:00','09:00','10:00','11:00','12:00','13:00','14:00']);

const fmtSlot = (s) => { const h=parseInt(s); return `${h>12?h-12:h===0?12:h}${h>=12?'PM':'AM'}`; };
const fmtTime12Top = (t) => { if (!t) return ''; const [h,m] = t.split(':').map(Number); const suf = h>=12?'PM':'AM'; const dh = h>12?h-12:h===0?12:h; return `${dh}:${String(m).padStart(2,'0')} ${suf}`; };
const fmtAmount = (a) => `₹${Number(a||0).toLocaleString('en-IN')}`;

const NAV = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'bookings', label: 'Bookings', icon: CalendarCheck },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'offline', label: 'Offline Reservation', icon: PhoneCall },
  { id: 'block', label: 'Block Slots', icon: Ban },
  { id: 'health', label: 'System Health', icon: Activity },
];

function AdminPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [page, setPage] = useState('dashboard');
  const [mobileNav, setMobileNav] = useState(false);

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center"><h2 className="text-xl font-bold mb-2">Access Denied</h2><p className="text-sm text-gray-500">Admin privileges required.</p>
          <button onClick={() => navigate('/dashboard')} className="mt-4 btn-primary text-sm px-4 py-2">Go to Dashboard</button>
        </div>
      </div>
    );
  }

  const PageComponent = { dashboard: DashboardPage, bookings: BookingsPage, users: UsersPage, offline: OfflinePage, block: BlockSlotsPage, health: HealthPage }[page];

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex" data-testid="admin-panel">
      <SEO
        title="Admin Panel"
        description="Malda Turf admin control center."
        path="/admin"
        noindex
      />
      {/* Sidebar - Desktop */}
      <aside className="hidden md:flex flex-col w-60 bg-[#0A0A0A] border-r border-[#1A1A1A] fixed top-0 left-0 bottom-0 z-40">
        {/* Brand — Admin Panel only */}
        <div className="p-5 border-b border-[#1A1A1A]">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-[#16A34A] flex items-center justify-center rounded-lg">
              <ShieldCheck className="h-4 w-4 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <span className="text-sm font-bold font-heading block tracking-tight leading-tight">Admin Panel</span>
              <span className="text-[9px] text-[#16A34A] font-body uppercase tracking-[0.18em]">Control Center</span>
            </div>
          </div>
        </div>

        {/* Live IST Clock */}
        <div className="px-4 py-3 border-b border-[#1A1A1A]">
          <ISTClock />
        </div>

        {/* Nav */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {NAV.map(n => (
            <button key={n.id} onClick={() => setPage(n.id)} data-testid={`admin-nav-${n.id}`}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13px] font-medium transition-all ${page === n.id ? 'bg-[#16A34A]/10 text-[#16A34A] border border-[#16A34A]/20' : 'text-neutral-500 hover:text-white hover:bg-[#111] border border-transparent'}`}>
              <n.icon className="h-4 w-4" />{n.label}
            </button>
          ))}
        </nav>

        {/* Footer: Return to User Panel + Branding */}
        <div className="border-t border-[#1A1A1A]">
          <div className="p-3 space-y-2">
            <CalendarSyncButton />
            <button
              onClick={() => navigate('/dashboard')}
              data-testid="return-to-user-panel"
              className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-[12px] font-semibold border border-[#1A1A1A] hover:border-[#16A34A]/40 hover:bg-[#16A34A]/5 hover:text-[#16A34A] text-neutral-300 transition-all group">
              <ArrowLeft className="h-3.5 w-3.5 group-hover:-translate-x-0.5 transition-transform" />
              Return to User Panel
            </button>
          </div>
          <div className="px-4 py-2 border-t border-[#1A1A1A]">
            <div className="flex items-center gap-2 mb-0.5">
              <div className="w-1.5 h-1.5 rounded-full bg-[#16A34A]"></div>
              <span className="text-[10px] text-neutral-500 font-body uppercase tracking-wider">Signed in</span>
            </div>
            <div className="text-[11px] text-neutral-300 font-body truncate" title={user?.email}>{user?.email}</div>
          </div>
          <div className="px-4 py-3 border-t border-[#1A1A1A] bg-[#0F0F0F]">
            <div className="text-[8px] text-neutral-600 uppercase tracking-[0.22em] font-heading mb-1">Imagination Partner</div>
            <a href="https://webintprojects.com" target="_blank" rel="noopener noreferrer" className="block group">
              <div className="text-[13px] font-bold text-white group-hover:text-[#16A34A] transition-colors font-heading tracking-tight leading-none">
                Web Int Projects
              </div>
            </a>
          </div>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#1A1A1A] h-14 flex items-center px-4 justify-between">
        <button onClick={() => setMobileNav(!mobileNav)} aria-label="Open menu"><Menu className="h-5 w-5 text-white" /></button>
        <span className="text-sm font-bold font-heading">{NAV.find(n=>n.id===page)?.label}</span>
        <ISTClock compact />
      </div>
      {mobileNav && (
        <div className="md:hidden fixed inset-0 z-50 bg-black/80" onClick={() => setMobileNav(false)}>
          <div className="w-60 bg-[#0A0A0A] h-full border-r border-[#1A1A1A] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b border-[#1A1A1A] flex items-center gap-2.5">
              <div className="w-9 h-9 bg-[#16A34A] flex items-center justify-center rounded-lg"><ShieldCheck className="h-4 w-4 text-white" strokeWidth={2.5} /></div>
              <div>
                <span className="text-sm font-bold font-heading block tracking-tight leading-tight">Admin Panel</span>
                <span className="text-[9px] text-[#16A34A] font-body uppercase tracking-[0.18em]">Control Center</span>
              </div>
            </div>
            <div className="px-4 py-3 border-b border-[#1A1A1A]"><ISTClock /></div>
            <nav className="flex-1 p-3 space-y-1">
              {NAV.map(n => (
                <button key={n.id} onClick={() => { setPage(n.id); setMobileNav(false); }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13px] font-medium ${page === n.id ? 'bg-[#16A34A]/10 text-[#16A34A]' : 'text-neutral-500'}`}>
                  <n.icon className="h-4 w-4" />{n.label}
                </button>
              ))}
            </nav>
            <div className="p-3 space-y-2 border-t border-[#1A1A1A]">
              <CalendarSyncButton />
              <button onClick={() => { setMobileNav(false); navigate('/dashboard'); }}
                className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-[12px] font-semibold border border-[#1A1A1A] text-neutral-300">
                <ArrowLeft className="h-3.5 w-3.5" /> Return to User Panel
              </button>
            </div>
            <div className="px-4 py-3 border-t border-[#1A1A1A] bg-[#0F0F0F]">
              <div className="text-[8px] text-neutral-600 uppercase tracking-[0.22em] font-heading mb-1">Imagination Partner</div>
              <div className="text-[13px] font-bold text-white font-heading tracking-tight leading-none">Web Int Projects</div>
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 md:ml-60 pt-14 md:pt-0 min-h-screen">
        <PageComponent />
      </main>
    </div>
  );
}

/* ─── DASHBOARD PAGE ─── */
function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/analytics`, { withCredentials: true })
      .then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[#16A34A]" /></div>;
  if (!data) return <div className="p-8 text-center text-neutral-500">Failed to load analytics</div>;

  const { online, offline, ratio } = data;
  const ratioData = [
    { name: 'Online', bookings: ratio.online_bookings, revenue: ratio.online_revenue },
    { name: 'Offline', bookings: ratio.offline_bookings, revenue: ratio.offline_revenue },
  ];

  return (
    <div className="p-6 md:p-8 max-w-6xl" data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold font-heading">Dashboard</h1>
        <span className="text-[11px] text-neutral-500 font-body">{data.date} IST</span>
      </div>

      {/* Online Stats */}
      <div className="mb-2"><span className="text-[10px] text-[#16A34A] uppercase tracking-wider font-semibold font-heading">Online</span></div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="Today Bookings" value={online.today.bookings} color="text-[#16A34A]" />
        <StatCard label="Today Revenue" value={fmtAmount(online.today.revenue)} color="text-[#16A34A]" />
        <StatCard label="This Month" value={online.month.bookings} sub={fmtAmount(online.month.revenue)} />
        <StatCard label="Monthly Revenue" value={fmtAmount(online.month.revenue)} />
      </div>

      {/* Offline Stats */}
      <div className="mb-2"><span className="text-[10px] text-blue-400 uppercase tracking-wider font-semibold font-heading">Offline</span></div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="Today Bookings" value={offline.today.bookings} color="text-blue-400" />
        <StatCard label="Today Revenue" value={fmtAmount(offline.today.revenue)} color="text-blue-400" />
        <StatCard label="This Month" value={offline.month.bookings} sub={fmtAmount(offline.month.revenue)} />
        <StatCard label="Monthly Revenue" value={fmtAmount(offline.month.revenue)} />
      </div>

      {/* Year Totals + Daily Avg */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
        <StatCard label="This Year Bookings" value={data.year_total_bookings} sub="Online + Offline" />
        <StatCard label="This Year Revenue" value={fmtAmount(data.year_total_revenue)} sub="Online + Offline" color="text-[#16A34A]" />
        <StatCard label="Daily Avg Revenue" value={fmtAmount(data.daily_avg_revenue)} sub="This year average" />
      </div>

      {/* Charts */}
      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <div className="bg-[#111] border border-[#1A1A1A] rounded-xl p-5">
          <h3 className="text-sm font-semibold mb-4 font-heading flex items-center gap-2"><TrendingUp className="h-4 w-4 text-[#16A34A]" />Last 7 Days Revenue</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.daily_revenue}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1A1A1A" />
                <XAxis dataKey="date" tick={{ fill: '#666', fontSize: 10 }} tickFormatter={d => d.slice(5)} />
                <YAxis tick={{ fill: '#666', fontSize: 10 }} />
                <Tooltip contentStyle={{ background: '#171717', border: '1px solid #262626', borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="online" name="Online" fill="#16A34A" radius={[4, 4, 0, 0]} />
                <Bar dataKey="offline" name="Offline" fill="#3B82F6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex items-center justify-center gap-4 mt-2">
            <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-[#16A34A]"></div><span className="text-[10px] text-neutral-500 font-body">Online</span></div>
            <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-[#3B82F6]"></div><span className="text-[10px] text-neutral-500 font-body">Offline</span></div>
          </div>
        </div>

        <div className="bg-[#111] border border-[#1A1A1A] rounded-xl p-5">
          <h3 className="text-sm font-semibold mb-4 font-heading flex items-center gap-2"><TrendingUp className="h-4 w-4 text-[#16A34A]" />Monthly Revenue</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.monthly_revenue}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1A1A1A" />
                <XAxis dataKey="month" tick={{ fill: '#666', fontSize: 10 }} tickFormatter={m => m.slice(5)} />
                <YAxis tick={{ fill: '#666', fontSize: 10 }} />
                <Tooltip contentStyle={{ background: '#171717', border: '1px solid #262626', borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="online" name="Online" fill="#16A34A" radius={[4, 4, 0, 0]} />
                <Bar dataKey="offline" name="Offline" fill="#3B82F6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex items-center justify-center gap-4 mt-2">
            <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-[#16A34A]"></div><span className="text-[10px] text-neutral-500 font-body">Online</span></div>
            <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-[#3B82F6]"></div><span className="text-[10px] text-neutral-500 font-body">Offline</span></div>
          </div>
        </div>
      </div>

      <div className="bg-[#111] border border-[#1A1A1A] rounded-xl p-5">
        <h3 className="text-sm font-semibold mb-4 font-heading">Online vs Offline (This Year)</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-[10px] text-neutral-500 uppercase tracking-wider mb-2 font-body">Bookings</div>
            <div className="h-32">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ratioData} layout="vertical">
                  <XAxis type="number" tick={{ fill: '#666', fontSize: 10 }} />
                  <YAxis type="category" dataKey="name" tick={{ fill: '#999', fontSize: 11 }} width={50} />
                  <Tooltip contentStyle={{ background: '#171717', border: '1px solid #262626', borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="bookings" fill="#16A34A" radius={[0, 4, 4, 0]}>
                    {ratioData.map((e, i) => <rect key={i} fill={i === 0 ? '#16A34A' : '#3B82F6'} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div>
            <div className="text-[10px] text-neutral-500 uppercase tracking-wider mb-2 font-body">Revenue</div>
            <div className="h-32">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ratioData} layout="vertical">
                  <XAxis type="number" tick={{ fill: '#666', fontSize: 10 }} />
                  <YAxis type="category" dataKey="name" tick={{ fill: '#999', fontSize: 11 }} width={50} />
                  <Tooltip contentStyle={{ background: '#171717', border: '1px solid #262626', borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="revenue" fill="#16A34A" radius={[0, 4, 4, 0]}>
                    {ratioData.map((e, i) => <rect key={i} fill={i === 0 ? '#16A34A' : '#3B82F6'} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 text-right"><span className="text-[10px] text-neutral-600 font-body">All times in IST</span></div>
    </div>
  );
}

function StatCard({ label, value, sub, color = 'text-white' }) {
  return (
    <div className="bg-[#111] border border-[#1A1A1A] rounded-xl p-4 hover:border-[#262626] transition-colors">
      <div className="text-[10px] text-neutral-500 uppercase tracking-wider font-body mb-2">{label}</div>
      <div className={`text-xl font-bold ${color}`} style={{ fontFamily: 'Inter, sans-serif' }}>{value}</div>
      {sub && <div className="text-[10px] text-neutral-600 mt-1 font-body">{sub}</div>}
    </div>
  );
}

/* ─── BOOKINGS PAGE (POLISHED) ─── */
function FilterDatePicker({ value, onChange }) {
  return (
    <div className="w-full">
      <TurfDatePicker
        value={value}
        onChange={onChange}
        placeholder="Filter by date"
        minDate={addDays(new Date(), -180)}
        maxDate={addDays(new Date(), 60)}
        testId="booking-date-filter"
        size="compact"
        className="w-full"
      />
    </div>
  );
}

function BookingsPage() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [verifyId, setVerifyId] = useState('');
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (search) params.set('search', search);
      if (dateFilter) params.set('date', dateFilter);
      const r = await axios.get(`${API_URL}/api/admin/bookings?${params}`, { withCredentials: true });
      setBookings(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [statusFilter, search, dateFilter]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const handleVerify = async () => {
    if (!verifyId.trim()) return;
    setVerifying(true); setVerifyResult(null);
    try {
      const booking = bookings.find(b => b.booking_id === verifyId.trim());
      if (booking && booking.status === 'confirmed') {
        setVerifyResult({ valid: true, booking });
      } else if (booking) {
        setVerifyResult({ valid: false, message: `Booking found but status is ${booking.status}` });
      } else {
        setVerifyResult({ valid: false, message: 'Booking not found' });
      }
    } catch { setVerifyResult({ valid: false, message: 'Verification failed' }); }
    setVerifying(false);
  };

  const statusCounts = { confirmed: 0, pending: 0, expired: 0 };
  bookings.forEach(b => { if (statusCounts[b.status] !== undefined) statusCounts[b.status]++; });

  const totalRevenue = bookings.filter(b => b.status === 'confirmed').reduce((s, b) => s + (b.total_amount || 0), 0);

  return (
    <div className="p-6 md:p-8 max-w-6xl" data-testid="admin-bookings">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold font-heading">Bookings</h1>
          <p className="text-[12px] text-neutral-500 font-body mt-0.5">Manage all online & offline reservations</p>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="Total" value={bookings.length} />
        <StatCard label="Confirmed" value={statusCounts.confirmed} color="text-[#16A34A]" />
        <StatCard label="Pending" value={statusCounts.pending} color="text-yellow-500" />
        <StatCard label="Revenue" value={fmtAmount(totalRevenue)} color="text-[#16A34A]" />
      </div>

      {/* Verify Ticket */}
      <div className="bg-[#111] border border-[#1A1A1A] rounded-xl p-5 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="h-3.5 w-3.5 text-[#16A34A]" />
          <span className="text-[10px] text-neutral-500 uppercase tracking-wider font-body">Verify Ticket</span>
        </div>
        <div className="flex gap-2">
          <input value={verifyId} onChange={e => setVerifyId(e.target.value)} placeholder="Enter Booking ID (e.g. book_abc123)" className="flex-1 text-sm" data-testid="verify-input" />
          <button onClick={handleVerify} disabled={verifying} className="btn-primary text-sm px-4 py-2 flex items-center gap-1.5" data-testid="verify-btn">
            {verifying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />} Verify
          </button>
        </div>
        {verifyResult && (
          <div className={`mt-3 flex items-center gap-2 p-3 rounded-lg text-sm ${verifyResult.valid ? 'bg-[#16A34A]/10 border border-[#16A34A]/20 text-[#16A34A]' : 'bg-red-500/10 border border-red-500/20 text-red-400'}`}>
            {verifyResult.valid ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
            {verifyResult.valid ? `Valid: ${verifyResult.booking.customer_name} · ${verifyResult.booking.booking_date} · ${fmtAmount(verifyResult.booking.total_amount)}` : verifyResult.message}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-[#111] border border-[#1A1A1A] rounded-xl p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
          {/* Search — wider */}
          <div className="md:col-span-5 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-500 pointer-events-none" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search name, phone, booking ID..."
              className="w-full h-10 pl-9 pr-3 text-[13px] bg-[#171717] border border-[#1A1A1A] rounded-lg focus:border-[#16A34A] outline-none transition-colors"
              style={{ padding: '0 0.75rem 0 2.25rem' }}
              data-testid="booking-search"
            />
          </div>

          {/* Date filter */}
          <div className="md:col-span-3 flex items-center gap-1.5">
            <div className="flex-1">
              <FilterDatePicker value={dateFilter} onChange={setDateFilter} />
            </div>
            {dateFilter && (
              <button
                onClick={() => setDateFilter('')}
                className="h-10 w-10 flex items-center justify-center bg-[#171717] border border-[#1A1A1A] rounded-lg text-neutral-500 hover:text-red-400 hover:border-red-500/30 transition-colors flex-shrink-0"
                data-testid="clear-date-filter"
                title="Clear date"
              >
                <XCircle className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* Status filter */}
          <div className="md:col-span-3">
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="w-full h-10 px-3 text-[13px] bg-[#171717] border border-[#1A1A1A] rounded-lg focus:border-[#16A34A] outline-none cursor-pointer"
              style={{ padding: '0 0.75rem' }}
              data-testid="booking-status-filter"
            >
              <option value="">All Status</option>
              <option value="confirmed">Confirmed ({statusCounts.confirmed})</option>
              <option value="pending">Pending ({statusCounts.pending})</option>
              <option value="expired">Expired ({statusCounts.expired})</option>
            </select>
          </div>

          {/* Refresh */}
          <div className="md:col-span-1">
            <button
              onClick={fetchBookings}
              className="w-full h-10 flex items-center justify-center gap-1.5 bg-[#171717] border border-[#1A1A1A] rounded-lg hover:border-[#16A34A]/40 hover:text-[#16A34A] text-neutral-400 transition-colors"
              data-testid="refresh-bookings"
              title="Refresh"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              <span className="md:hidden text-[13px]">Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {/* Booking List */}
      {loading ? <div className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[#16A34A]" /></div> : (
        <div className="space-y-2">
          {bookings.length === 0 && (
            <div className="bg-[#111] border border-dashed border-[#1A1A1A] rounded-xl p-12 text-center">
              <CalendarCheck className="h-8 w-8 text-neutral-700 mx-auto mb-3" />
              <p className="text-sm text-neutral-500 font-body">No bookings found</p>
            </div>
          )}
          {bookings.map(b => {
            const isExp = expanded === b.booking_id;
            const slots = b.time_slots || [];
            const happyC = slots.filter(s => HAPPY_SET.has(s)).length;
            const rushC = slots.length - happyC;
            const statusClr = b.status === 'confirmed' ? 'text-[#16A34A] bg-[#16A34A]/5 border-[#16A34A]/20' : b.status === 'pending' ? 'text-yellow-500 bg-yellow-500/5 border-yellow-500/20' : 'text-red-400 bg-red-500/5 border-red-500/20';
            return (
              <div key={b.booking_id} className={`bg-[#111] border ${isExp ? 'border-[#16A34A]/30' : 'border-[#1A1A1A]'} rounded-xl overflow-hidden transition-all hover:border-[#262626]`} data-testid={`admin-booking-${b.booking_id}`}>
                <button onClick={() => setExpanded(isExp ? null : b.booking_id)} className="w-full p-4 text-left">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#16A34A]/10 border border-[#16A34A]/20 flex items-center justify-center">
                        <span className="text-[13px] font-bold text-[#16A34A] font-heading">
                          {(b.customer_name || 'U').charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-sm font-semibold truncate">{b.customer_name || 'Unknown'}</span>
                          <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border rounded ${statusClr} flex-shrink-0`}>{b.status}</span>
                          {b.payment_status === 'offline' && <span className="text-[9px] bg-blue-500/10 text-blue-400 border border-blue-500/20 px-1.5 py-0.5 rounded font-semibold uppercase flex-shrink-0">Offline</span>}
                          {b.is_custom_time && <span className="text-[9px] bg-purple-500/10 text-purple-400 border border-purple-500/20 px-1.5 py-0.5 rounded font-semibold uppercase flex-shrink-0">Custom</span>}
                        </div>
                        <div className="flex items-center gap-2 text-[11px] text-neutral-500 font-body">
                          <span>{b.booking_date}</span>
                          <span className="text-neutral-700">·</span>
                          {b.is_custom_time && b.custom_start_time && b.custom_end_time ? (
                            <span className="text-purple-400">{fmtTime12Top(b.custom_start_time)} – {fmtTime12Top(b.custom_end_time)} ({b.total_hours}hr)</span>
                          ) : (
                            <>
                              <span>{slots.length} slot{slots.length !== 1 ? 's' : ''}</span>
                              {happyC > 0 && <><span className="text-neutral-700">·</span><span className="text-[#16A34A]">Happy×{happyC}</span></>}
                              {rushC > 0 && <><span className="text-neutral-700">·</span><span className="text-orange-400">Rush×{rushC}</span></>}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-sm font-bold" style={{ fontFamily: 'Inter, sans-serif' }}>{fmtAmount(b.total_amount)}</span>
                      {isExp ? <ChevronUp className="h-4 w-4 text-neutral-500" /> : <ChevronDown className="h-4 w-4 text-neutral-500" />}
                    </div>
                  </div>
                </button>
                {isExp && (
                  <div className="border-t border-[#1A1A1A] p-4 bg-[#0F0F0F] space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                      <div><span className="text-neutral-600 font-body block text-[10px] uppercase tracking-wider mb-1">Booking ID</span><span className="font-mono text-neutral-300">{b.booking_id}</span></div>
                      <div><span className="text-neutral-600 font-body block text-[10px] uppercase tracking-wider mb-1">Phone</span><span>{b.customer_phone}</span></div>
                      <div><span className="text-neutral-600 font-body block text-[10px] uppercase tracking-wider mb-1">Email</span><span className="truncate block">{b.customer_email || '-'}</span></div>
                      <div><span className="text-neutral-600 font-body block text-[10px] uppercase tracking-wider mb-1">Sport</span><span className="capitalize">{b.sport || 'cricket'}</span></div>
                    </div>
                    <div>
                      <span className="text-neutral-600 font-body block text-[10px] uppercase tracking-wider mb-2">{b.is_custom_time ? 'Custom Window' : 'Slots'}</span>
                      {b.is_custom_time && b.custom_start_time && b.custom_end_time ? (
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="text-[14px] font-semibold text-purple-400">{fmtTime12Top(b.custom_start_time)} – {fmtTime12Top(b.custom_end_time)}</span>
                          <span className="text-[11px] text-neutral-500">{b.total_hours} hr{b.total_hours !== 1 ? 's' : ''}</span>
                          <span className="text-[10px] text-neutral-600">Blocks:</span>
                          <div className="flex flex-wrap gap-1.5">
                            {slots.map(s => (
                              <span key={s} className="px-2 py-1 rounded-md text-[11px] font-medium border bg-purple-500/5 border-purple-500/20 text-purple-300">
                                {fmtSlot(s)}
                              </span>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {slots.map(s => (
                            <span key={s} className={`px-2 py-1 rounded-md text-[11px] font-medium border ${HAPPY_SET.has(s) ? 'bg-[#16A34A]/5 border-[#16A34A]/20 text-[#16A34A]' : 'bg-orange-500/5 border-orange-500/20 text-orange-400'}`}>
                              {fmtSlot(s)}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 pt-2">
                      <button onClick={() => generateReceipt(b)} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#1A1A1A] border border-[#262626] rounded-lg text-xs hover:border-[#16A34A] hover:text-[#16A34A] transition-colors">
                        <Download className="h-3 w-3" /> Download Receipt
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─── USERS PAGE (POLISHED) ─── */
function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [promoting, setPromoting] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/users?search=${search}`, { withCredentials: true });
      setUsers(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [search]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const toggleRole = async (userId, currentRole) => {
    setPromoting(userId);
    try {
      const endpoint = currentRole === 'admin' ? '/api/admin/demote-user' : '/api/admin/promote-user';
      await axios.post(`${API_URL}${endpoint}`, { user_id: userId }, { withCredentials: true });
      fetchUsers();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    setPromoting(null);
  };

  const adminCount = users.filter(u => u.role === 'admin').length;

  return (
    <div className="p-6 md:p-8 max-w-6xl" data-testid="admin-users">
      <div className="mb-6">
        <h1 className="text-2xl font-bold font-heading">Users</h1>
        <p className="text-[12px] text-neutral-500 font-body mt-0.5">Manage user accounts & admin access</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
        <StatCard label="Total Users" value={users.length} />
        <StatCard label="Admins" value={adminCount} color="text-[#16A34A]" />
        <StatCard label="Customers" value={users.length - adminCount} />
      </div>

      <div className="bg-[#111] border border-[#1A1A1A] rounded-xl p-4 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-500" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, email, phone..." className="w-full pl-9 text-sm" data-testid="user-search" />
        </div>
      </div>

      {loading ? <div className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[#16A34A]" /></div> : (
        <div className="grid md:grid-cols-2 gap-3" data-testid="user-list">
          {users.length === 0 && (
            <div className="md:col-span-2 bg-[#111] border border-dashed border-[#1A1A1A] rounded-xl p-12 text-center">
              <Users className="h-8 w-8 text-neutral-700 mx-auto mb-3" />
              <p className="text-sm text-neutral-500 font-body">No users found</p>
            </div>
          )}
          {users.map(u => (
            <div key={u.user_id} className="bg-[#111] border border-[#1A1A1A] hover:border-[#262626] rounded-xl p-4 flex items-center justify-between gap-3 transition-all group" data-testid={`user-row-${u.user_id}`}>
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-105 ${u.role === 'admin' ? 'bg-[#16A34A]/10 border border-[#16A34A]/20' : 'bg-[#1A1A1A] border border-[#262626]'}`}>
                  {u.role === 'admin'
                    ? <ShieldCheck className="h-4 w-4 text-[#16A34A]" />
                    : <span className="text-[13px] font-bold text-neutral-400 font-heading">{(u.name || u.email || 'U').charAt(0).toUpperCase()}</span>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="text-sm font-semibold truncate">{u.name || 'Unknown'}</span>
                    {u.role === 'admin' && <span className="text-[9px] bg-[#16A34A]/10 text-[#16A34A] border border-[#16A34A]/20 px-1.5 py-0.5 rounded font-bold uppercase flex-shrink-0">Admin</span>}
                  </div>
                  <span className="text-[11px] text-neutral-500 font-body truncate block">{u.email}</span>
                  {u.phone && <span className="text-[10px] text-neutral-600 font-body block">{u.phone}</span>}
                </div>
              </div>
              <button onClick={() => toggleRole(u.user_id, u.role)} disabled={promoting === u.user_id} data-testid={`toggle-role-${u.user_id}`}
                className={`flex-shrink-0 text-[11px] font-semibold px-3 py-1.5 rounded-lg border transition-colors ${u.role === 'admin' ? 'border-red-500/20 text-red-400 hover:bg-red-500/10' : 'border-[#16A34A]/20 text-[#16A34A] hover:bg-[#16A34A]/10'}`}>
                {promoting === u.user_id ? <Loader2 className="h-3 w-3 animate-spin" /> : u.role === 'admin' ? 'Demote' : 'Make Admin'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── OFFLINE RESERVATION (MULTI-STEP WIZARD) ─── */
const STEP_LABELS = ['Customer Info', 'Date & Slots', 'Confirm'];

function OfflinePage() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ customer_name: '', customer_phone: '', customer_email: '', booking_date: '', time_slots: [], sport: 'cricket', note: '', payment_mode: 'fully_paid', amount_paid: '', is_custom_time: false, custom_start_time: '', custom_end_time: '', use_custom_rate: false, custom_rate: '', rate_label: '' });
  const [bookedSlots, setBookedSlots] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (form.booking_date) {
      axios.get(`${API_URL}/api/turfs/turf_cricket01/availability?date=${form.booking_date}`, { withCredentials: true })
        .then(r => setBookedSlots(r.data.booked_slots)).catch(() => {});
    }
  }, [form.booking_date]);

  const toggleSlot = (slot) => {
    if (bookedSlots.includes(slot)) return;
    const slots = form.time_slots.includes(slot) ? form.time_slots.filter(s => s !== slot) : [...form.time_slots, slot];
    setForm({ ...form, time_slots: slots });
  };

  const isWeekend = form.booking_date ? [0, 6].includes(new Date(form.booking_date + 'T00:00:00').getDay()) : false;
  const getRate = (slot) => HAPPY_SET.has(slot) ? (isWeekend ? 400 : 300) : (isWeekend ? 500 : 400);

  // Custom-time helpers
  const parseHHMM = (t) => {
    if (!t || !t.includes(':')) return null;
    const [h, m] = t.split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return h * 60 + m;
  };
  const customDuration = (() => {
    if (!form.is_custom_time) return 0;
    const s = parseHHMM(form.custom_start_time);
    const e = parseHHMM(form.custom_end_time);
    if (s == null || e == null || e <= s) return 0;
    return (e - s) / 60;
  })();
  const customAffectedSlots = (() => {
    if (!form.is_custom_time) return [];
    const s = parseHHMM(form.custom_start_time);
    const e = parseHHMM(form.custom_end_time);
    if (s == null || e == null || e <= s) return [];
    const out = [];
    let h = Math.floor(s / 60);
    while (h * 60 < e) {
      const slotStart = h * 60;
      const slotEnd = (h + 1) * 60;
      if (slotStart < e && slotEnd > s) out.push(`${String(h % 24).padStart(2, '0')}:00`);
      h += 1;
    }
    return out;
  })();
  const customConflicts = customAffectedSlots.filter(s => bookedSlots.includes(s));
  const customRate = form.is_custom_time && form.custom_start_time
    ? getRate(`${form.custom_start_time.split(':')[0].padStart(2, '0')}:00`)
    : 0;
  const customTotal = form.is_custom_time ? Math.round(customDuration * customRate) : 0;

  const total = (() => {
    if (form.use_custom_rate && Number(form.custom_rate) > 0) {
      const hours = form.is_custom_time ? customDuration : form.time_slots.length;
      return Math.round(Number(form.custom_rate) * hours);
    }
    return form.is_custom_time
      ? customTotal
      : form.time_slots.reduce((s, slot) => s + getRate(slot), 0);
  })();
  const autoTotal = form.is_custom_time
    ? customTotal
    : form.time_slots.reduce((s, slot) => s + getRate(slot), 0);
  const happyC = form.time_slots.filter(s => HAPPY_SET.has(s)).length;
  const rushC = form.time_slots.length - happyC;

  const fmtTime12 = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':').map(Number);
    const suf = h >= 12 ? 'PM' : 'AM';
    const dh = h > 12 ? h - 12 : h === 0 ? 12 : h;
    return `${dh}:${String(m).padStart(2, '0')} ${suf}`;
  };

  const canStep1 = form.customer_name.trim() && form.customer_phone.trim().length === 10;
  const canStep2 = form.is_custom_time
    ? (!!form.booking_date && customDuration >= 0.5 && customAffectedSlots.length > 0 && customConflicts.length === 0)
    : (!!form.booking_date && form.time_slots.length > 0);
  // Step-3 validation: if partial, require a positive amount <= total and < total
  const partialValid = form.payment_mode === 'fully_paid'
    || (Number(form.amount_paid) > 0 && Number(form.amount_paid) < total);
  const canStep3 = partialValid;

  const resetAll = () => {
    setForm({ customer_name: '', customer_phone: '', customer_email: '', booking_date: '', time_slots: [], sport: 'cricket', note: '', payment_mode: 'fully_paid', amount_paid: '', is_custom_time: false, custom_start_time: '', custom_end_time: '', use_custom_rate: false, custom_rate: '', rate_label: '' });
    setStep(1); setResult(null); setBookedSlots([]);
  };

  const handleSubmit = async () => {
    if (!canStep3) {
      alert('Enter a valid partial amount (greater than 0 and less than total)');
      return;
    }
    setSubmitting(true); setResult(null);
    try {
      const payload = {
        ...form,
        amount_paid: form.payment_mode === 'fully_paid' ? total : Number(form.amount_paid || 0),
        // Backend validates these; only send the ones relevant to chosen mode
        time_slots: form.is_custom_time ? [] : form.time_slots,
        custom_rate: form.use_custom_rate ? Number(form.custom_rate || 0) : 0,
        rate_label: form.use_custom_rate ? (form.rate_label || '').trim() : '',
      };
      const r = await axios.post(`${API_URL}/api/admin/offline-reservation`, payload, { withCredentials: true });
      setResult(r.data);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to create reservation');
    }
    setSubmitting(false);
  };

  /* SUCCESS SCREEN */
  if (result) {
    return (
      <div className="p-6 md:p-8 max-w-2xl" data-testid="admin-offline">
        <div className="bg-[#111] border border-[#16A34A]/30 rounded-2xl overflow-hidden">
          <div className="h-1 bg-[#16A34A]"></div>
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-[#16A34A] rounded-full flex items-center justify-center mx-auto mb-5">
              <CheckCircle className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold mb-1 font-heading">Reservation Confirmed</h2>
            <p className="text-sm text-neutral-500 font-body mb-6">SMS confirmation sent to {form.customer_phone}</p>

            <div className="bg-[#0F0F0F] border border-[#1A1A1A] rounded-xl p-5 mb-6 text-left space-y-3">
              <Row label="Booking ID" value={<span className="font-mono text-[12px]">{result.booking_id}</span>} />
              <Row label="Customer" value={result.customer_name} />
              <Row label="Date" value={result.booking_date} />
              {result.is_custom_time ? (
                <Row label="Window" value={<span className="font-semibold">{fmtTime12(result.custom_start_time)} – {fmtTime12(result.custom_end_time)} <span className="text-neutral-500 ml-1">({result.total_hours} hr{result.total_hours !== 1 ? 's' : ''})</span></span>} />
              ) : (
                <Row label="Slots" value={`${happyC > 0 ? `Happy×${happyC}` : ''}${happyC > 0 && rushC > 0 ? ' · ' : ''}${rushC > 0 ? `Rush×${rushC}` : ''}`} />
              )}
              {result.rate_override_applied && (
                <Row label="Rate" value={<span><span className="text-[#16A34A] font-semibold">₹{result.hourly_rate}/hr</span>{result.rate_label ? <span className="text-[11px] text-neutral-500 ml-1.5">· {result.rate_label}</span> : null}</span>} />
              )}
              <div className="h-px bg-[#1A1A1A]"></div>
              <Row label="Total" value={<span className="text-xl font-bold text-[#16A34A]" style={{ fontFamily: 'Inter, sans-serif' }}>{fmtAmount(result.total_amount)}</span>} />
              {result.payment_mode === 'partially_paid' && (
                <>
                  <Row label="Paid" value={<span className="text-[#16A34A] font-semibold">{fmtAmount(result.amount_paid)}</span>} />
                  <Row label="Due" value={<span className="text-yellow-400 font-semibold">{fmtAmount(result.amount_due)}</span>} />
                </>
              )}
              <Row
                label="Status"
                value={
                  result.payment_mode === 'partially_paid'
                    ? <span className="inline-block bg-yellow-500/10 text-yellow-400 border border-yellow-500/30 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider">Partially Paid</span>
                    : <span className="inline-block bg-[#16A34A]/10 text-[#16A34A] border border-[#16A34A]/30 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider">Fully Paid</span>
                }
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => generateReceipt(result)} data-testid="offline-receipt-btn" className="btn-primary inline-flex items-center justify-center gap-2">
                <Download className="h-4 w-4" /> Receipt
              </button>
              <button onClick={resetAll} data-testid="offline-new-btn" className="btn-secondary inline-flex items-center justify-center gap-2">
                <PhoneCall className="h-4 w-4" /> New
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-3xl" data-testid="admin-offline">
      <div className="mb-6">
        <h1 className="text-2xl font-bold font-heading">Offline Reservation</h1>
        <p className="text-[12px] text-neutral-500 font-body mt-0.5">Walk-in / phone / WhatsApp bookings · Confirmation SMS sent automatically</p>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-8" data-testid="offline-stepper">
        {STEP_LABELS.map((label, i) => {
          const num = i + 1;
          const active = step >= num;
          const completed = step > num;
          return (
            <React.Fragment key={num}>
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold transition-all ${completed ? 'bg-[#16A34A] text-white' : active ? 'bg-[#16A34A]/20 border border-[#16A34A] text-[#16A34A]' : 'bg-[#1A1A1A] text-neutral-600'}`}>
                  {completed ? <CheckCircle className="h-4 w-4" /> : num}
                </div>
                <span className={`text-[12px] font-body hidden sm:inline ${active ? 'text-white font-semibold' : 'text-neutral-600'}`}>{label}</span>
              </div>
              {num < STEP_LABELS.length && <div className={`flex-1 h-px ${step > num ? 'bg-[#16A34A]' : 'bg-[#1A1A1A]'} transition-colors`} />}
            </React.Fragment>
          );
        })}
      </div>

      {/* STEP 1: Customer Info */}
      {step === 1 && (
        <div className="bg-[#111] border border-[#1A1A1A] rounded-xl p-6 space-y-5" data-testid="offline-step-1">
          <div className="flex items-center gap-2 mb-1">
            <User className="h-4 w-4 text-[#16A34A]" />
            <span className="text-[10px] text-neutral-500 uppercase tracking-wider font-body">Step 1 — Customer Details</span>
          </div>
          <FieldLabel icon={User} label="Name *">
            <input value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })} placeholder="Player / Team name" className="w-full text-sm" data-testid="offline-name" />
          </FieldLabel>
          <FieldLabel icon={Phone} label="Phone *">
            <div className="flex">
              <div className="flex items-center px-3 bg-[#1A1A1A] border border-r-0 border-[#262626] text-sm text-neutral-400 font-body rounded-l-lg">+91</div>
              <input value={form.customer_phone} onChange={e => setForm({ ...form, customer_phone: e.target.value.replace(/\D/g, '').slice(0, 10) })} placeholder="9876543210" className="w-full text-sm rounded-l-none" data-testid="offline-phone" />
            </div>
            {form.customer_phone && form.customer_phone.length < 10 && <p className="text-[11px] text-yellow-500/70 mt-1 font-body">Enter 10-digit number</p>}
          </FieldLabel>
          <FieldLabel icon={Mail} label="Email (optional)">
            <input value={form.customer_email} onChange={e => setForm({ ...form, customer_email: e.target.value })} placeholder="customer@example.com" className="w-full text-sm" data-testid="offline-email" />
          </FieldLabel>
          <FieldLabel icon={Trophy} label="Sport">
            <div className="grid grid-cols-2 gap-2">
              {['cricket', 'football'].map(s => (
                <button key={s} onClick={() => setForm({ ...form, sport: s })} data-testid={`offline-sport-${s}`}
                  className={`py-2.5 rounded-lg text-sm font-medium border capitalize transition-all ${form.sport === s ? 'bg-[#16A34A]/10 border-[#16A34A]/40 text-[#16A34A]' : 'bg-[#171717] border-[#1A1A1A] text-neutral-400 hover:border-[#262626]'}`}>
                  {s}
                </button>
              ))}
            </div>
          </FieldLabel>
          <FieldLabel icon={MessageSquare} label="Note (optional)">
            <input value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} placeholder="Special instructions" className="w-full text-sm" data-testid="offline-note" />
          </FieldLabel>

          <div className="flex justify-end pt-2">
            <button onClick={() => setStep(2)} disabled={!canStep1} data-testid="offline-next-1"
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed">
              Next: Date & Slots <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* STEP 2: Date & Slots */}
      {step === 2 && (
        <div className="space-y-4" data-testid="offline-step-2">
          <div className="bg-[#111] border border-[#1A1A1A] rounded-xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <CalendarCheck className="h-4 w-4 text-[#16A34A]" />
              <span className="text-[10px] text-neutral-500 uppercase tracking-wider font-body">Step 2 — Select Date</span>
            </div>
            <TurfDatePicker
              value={form.booking_date}
              onChange={(d) => setForm({ ...form, booking_date: d, time_slots: [], custom_start_time: '', custom_end_time: '' })}
              showWeekendBadge
              testId="offline-date-picker"
            />
          </div>

          {form.booking_date && (
            <div className="bg-[#111] border border-[#1A1A1A] rounded-xl p-4 sm:p-6">
              {/* Mode toggle: Slot Grid vs Custom Time (admin-only) */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 pb-4 border-b border-[#1A1A1A] gap-3">
                <div className="text-[12px] font-semibold font-heading">Booking mode</div>
                <div className="flex bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg p-0.5 self-start sm:self-auto">
                  <button
                    type="button"
                    data-testid="offline-mode-slots"
                    onClick={() => setForm({ ...form, is_custom_time: false, custom_start_time: '', custom_end_time: '' })}
                    className={`px-3 py-1.5 rounded-md text-[11px] font-semibold transition-all ${!form.is_custom_time ? 'bg-[#16A34A]/15 text-[#16A34A]' : 'text-neutral-500 hover:text-white'}`}>
                    Hourly Slots
                  </button>
                  <button
                    type="button"
                    data-testid="offline-mode-custom"
                    onClick={() => setForm({ ...form, is_custom_time: true, time_slots: [] })}
                    className={`px-3 py-1.5 rounded-md text-[11px] font-semibold transition-all ${form.is_custom_time ? 'bg-[#16A34A]/15 text-[#16A34A]' : 'text-neutral-500 hover:text-white'}`}>
                    Custom Time
                  </button>
                </div>
              </div>

              {/* Custom Time mode */}
              {form.is_custom_time && (
                <div className="space-y-4" data-testid="offline-custom-time-panel">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] text-neutral-400 uppercase tracking-wider mb-1.5 font-body">Start Time</label>
                      <TurfTimePicker
                        value={form.custom_start_time}
                        onChange={(t) => setForm({ ...form, custom_start_time: t })}
                        placeholder="Pick start"
                        testId="custom-start-time"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] text-neutral-400 uppercase tracking-wider mb-1.5 font-body">End Time</label>
                      <TurfTimePicker
                        value={form.custom_end_time}
                        onChange={(t) => setForm({ ...form, custom_end_time: t })}
                        placeholder="Pick end"
                        testId="custom-end-time"
                      />
                    </div>
                  </div>

                  {/* Live preview of custom booking */}
                  {form.custom_start_time && form.custom_end_time && customDuration > 0 && (
                    <div className="bg-[#0F0F0F] border border-[#1A1A1A] rounded-lg p-4" data-testid="custom-preview">
                      <div className="text-[10px] text-neutral-500 uppercase tracking-wider font-body mb-3">Preview</div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 mb-3">
                        <div className="flex flex-col">
                          <span className="text-[10px] text-neutral-500 uppercase tracking-wider font-body">Window</span>
                          <span className="text-[13px] font-semibold mt-0.5">{fmtTime12(form.custom_start_time)} – {fmtTime12(form.custom_end_time)}</span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[10px] text-neutral-500 uppercase tracking-wider font-body">Duration</span>
                          <span className="text-[13px] font-semibold mt-0.5">{customDuration} hr{customDuration !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[10px] text-neutral-500 uppercase tracking-wider font-body">Rate</span>
                          <span className="text-[13px] font-semibold text-[#16A34A] mt-0.5">₹{customRate}/hr</span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[10px] text-neutral-500 uppercase tracking-wider font-body">Total</span>
                          <span className="text-[18px] font-bold text-[#16A34A] mt-0.5" style={{ fontFamily: 'Inter, sans-serif' }}>{fmtAmount(customTotal)}</span>
                        </div>
                      </div>
                      <div className="pt-3 border-t border-[#1A1A1A]">
                        <div className="text-[10px] text-neutral-500 uppercase tracking-wider font-body mb-2">Will block from online booking</div>
                        <div className="flex flex-wrap gap-1.5">
                          {customAffectedSlots.map(s => {
                            const isConflict = customConflicts.includes(s);
                            return (
                              <span key={s} className={`px-2 py-1 rounded-md text-[11px] font-medium border ${isConflict ? 'bg-red-500/10 border-red-500/30 text-red-400 line-through' : HAPPY_SET.has(s) ? 'bg-[#16A34A]/5 border-[#16A34A]/20 text-[#16A34A]' : 'bg-orange-500/5 border-orange-500/20 text-orange-400'}`}>
                                {fmtSlot(s)}{isConflict ? ' (booked)' : ''}
                              </span>
                            );
                          })}
                        </div>
                        {customConflicts.length > 0 && (
                          <p className="mt-2 text-[11px] text-red-400 font-body">Conflict — one or more overlapping slots are already booked. Pick a different window.</p>
                        )}
                      </div>
                    </div>
                  )}
                  {form.custom_start_time && form.custom_end_time && customDuration <= 0 && (
                    <p className="text-[11px] text-yellow-500/80 font-body">End time must be after start time (15-minute increments).</p>
                  )}
                  {form.custom_start_time && form.custom_end_time && customDuration > 0 && customDuration < 0.5 && (
                    <p className="text-[11px] text-yellow-500/80 font-body">Minimum duration is 30 minutes.</p>
                  )}
                </div>
              )}

              {/* Slot Grid mode */}
              {!form.is_custom_time && (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-[10px] text-neutral-500 uppercase tracking-wider font-body">
                      Available Slots · {isWeekend ? 'Weekend' : 'Weekday'} Rates
                    </div>
                    {form.time_slots.length > 0 && (
                      <span className="text-[11px] text-[#16A34A] font-body">{form.time_slots.length} selected</span>
                    )}
                  </div>
                  <div className="mb-4">
                    <div className="text-[11px] text-neutral-500 font-body mb-2 flex items-center gap-1.5"><Clock className="h-3 w-3 text-[#16A34A]" />Happy Hours · ₹{isWeekend ? '400' : '300'}/hr</div>
                    <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5">
                      {ALL_SLOTS.filter(s => HAPPY_SET.has(s)).map(slot => {
                        const booked = bookedSlots.includes(slot);
                        const sel = form.time_slots.includes(slot);
                        return <button key={slot} onClick={() => toggleSlot(slot)} disabled={booked} data-testid={`offline-slot-${slot}`}
                          className={`py-2 rounded-lg text-[11px] text-center transition-all ${booked ? 'bg-[#0A0A0A] text-neutral-700 line-through cursor-not-allowed' : sel ? 'bg-[#16A34A] text-white font-semibold scale-105' : 'bg-[#171717] border border-[#1A1A1A] text-neutral-400 hover:border-[#16A34A]/40 hover:text-[#16A34A]'}`}>
                          {fmtSlot(slot)}
                        </button>;
                      })}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] text-neutral-500 font-body mb-2 flex items-center gap-1.5"><Zap className="h-3 w-3 text-orange-400" />Rush Hours · ₹{isWeekend ? '500' : '400'}/hr</div>
                    <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5">
                      {ALL_SLOTS.filter(s => !HAPPY_SET.has(s)).map(slot => {
                        const booked = bookedSlots.includes(slot);
                        const sel = form.time_slots.includes(slot);
                        return <button key={slot} onClick={() => toggleSlot(slot)} disabled={booked} data-testid={`offline-slot-${slot}`}
                          className={`py-2 rounded-lg text-[11px] text-center transition-all ${booked ? 'bg-[#0A0A0A] text-neutral-700 line-through cursor-not-allowed' : sel ? 'bg-[#16A34A] text-white font-semibold scale-105' : 'bg-[#171717] border border-[#1A1A1A] text-neutral-400 hover:border-[#16A34A]/40 hover:text-[#16A34A]'}`}>
                          {fmtSlot(slot)}
                        </button>;
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          <div className="flex justify-between gap-2">
            <button onClick={() => setStep(1)} className="btn-secondary inline-flex items-center gap-2" data-testid="offline-back-2">
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            <button onClick={() => setStep(3)} disabled={!canStep2} data-testid="offline-next-2"
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed">
              Review & Confirm <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* STEP 3: Confirm */}
      {step === 3 && (
        <div className="space-y-4" data-testid="offline-step-3">
          <div className="bg-[#111] border border-[#1A1A1A] rounded-xl p-6">
            <div className="flex items-center gap-2 mb-5">
              <FileText className="h-4 w-4 text-[#16A34A]" />
              <span className="text-[10px] text-neutral-500 uppercase tracking-wider font-body">Step 3 — Review</span>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <div className="space-y-3">
                <div className="text-[10px] text-neutral-500 uppercase tracking-wider font-body">Customer</div>
                <Row label="Name" value={form.customer_name} />
                <Row label="Phone" value={`+91 ${form.customer_phone}`} />
                {form.customer_email && <Row label="Email" value={form.customer_email} />}
                <Row label="Sport" value={<span className="capitalize">{form.sport}</span>} />
                {form.note && <Row label="Note" value={form.note} />}
              </div>
              <div className="space-y-3">
                <div className="text-[10px] text-neutral-500 uppercase tracking-wider font-body">Booking</div>
                <Row label="Date" value={`${form.booking_date} ${isWeekend ? '(Weekend)' : '(Weekday)'}`} />
                {form.is_custom_time ? (
                  <>
                    <Row label="Mode" value={<span className="text-[#16A34A] font-semibold">Custom Time</span>} />
                    <Row label="Window" value={<span className="font-semibold">{fmtTime12(form.custom_start_time)} – {fmtTime12(form.custom_end_time)}</span>} />
                    <Row label="Duration" value={`${customDuration} hr${customDuration !== 1 ? 's' : ''}`} />
                    <Row label="Rate" value={`₹${customRate}/hr`} />
                  </>
                ) : (
                  <>
                    <Row label="Slots" value={`${happyC > 0 ? `Happy×${happyC}` : ''}${happyC > 0 && rushC > 0 ? ' · ' : ''}${rushC > 0 ? `Rush×${rushC}` : ''}`} />
                    <Row label="Hours" value={`${form.time_slots.length} hr${form.time_slots.length !== 1 ? 's' : ''}`} />
                  </>
                )}
                <div className="h-px bg-[#1A1A1A]"></div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-neutral-500 font-body">Total</span>
                  <span className="text-2xl font-bold text-[#16A34A]" style={{ fontFamily: 'Inter, sans-serif' }}>{fmtAmount(total)}</span>
                </div>
              </div>
            </div>
            <div className="mt-5 pt-5 border-t border-[#1A1A1A]">
              <div className="text-[10px] text-neutral-500 uppercase tracking-wider font-body mb-2">{form.is_custom_time ? 'Will block from online' : 'Selected Slots'}</div>
              <div className="flex flex-wrap gap-1.5">
                {(form.is_custom_time ? customAffectedSlots : form.time_slots).map(s => (
                  <span key={s} className={`px-2 py-1 rounded-md text-[11px] font-medium border ${HAPPY_SET.has(s) ? 'bg-[#16A34A]/5 border-[#16A34A]/20 text-[#16A34A]' : 'bg-orange-500/5 border-orange-500/20 text-orange-400'}`}>
                    {fmtSlot(s)}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Pricing — auto or custom rate override */}
          <div className="bg-[#111] border border-[#1A1A1A] rounded-xl p-6">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-[#16A34A]" />
                <span className="text-[10px] text-neutral-500 uppercase tracking-wider font-body">Pricing</span>
              </div>
              <button
                type="button"
                onClick={() => setForm({ ...form, use_custom_rate: !form.use_custom_rate, custom_rate: '', rate_label: '' })}
                data-testid="toggle-custom-rate"
                className="inline-flex items-center gap-2 cursor-pointer select-none"
              >
                <span className="text-[11px] text-neutral-400 font-body">Custom rate</span>
                <span
                  className={`relative inline-block w-9 h-5 rounded-full transition-colors ${form.use_custom_rate ? 'bg-[#16A34A]' : 'bg-[#1A1A1A] border border-[#262626]'}`}>
                  <span className={`absolute top-0.5 ${form.use_custom_rate ? 'left-[18px]' : 'left-0.5'} w-4 h-4 rounded-full bg-white transition-all`} />
                </span>
              </button>
            </div>

            {!form.use_custom_rate ? (
              <div className="text-[12px] text-neutral-500 font-body">
                Auto-calculated · <span className="text-neutral-300">{form.is_custom_time ? `₹${customRate}/hr × ${customDuration} hr${customDuration !== 1 ? 's' : ''}` : `${form.time_slots.length} slot${form.time_slots.length !== 1 ? 's' : ''}`}</span>
                {' '}· Total <span className="text-[#16A34A] font-semibold">{fmtAmount(autoTotal)}</span>
              </div>
            ) : (
              <div className="space-y-3" data-testid="custom-rate-panel">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] text-neutral-500 uppercase tracking-wider mb-1.5 font-body">Rate per hour (₹)</label>
                    <div className="flex">
                      <div className="flex items-center px-3 bg-[#1A1A1A] border border-r-0 border-[#262626] text-sm text-neutral-400 font-body rounded-l-lg">₹</div>
                      <input
                        type="number"
                        min="1"
                        max="10000"
                        step="50"
                        value={form.custom_rate}
                        onChange={e => setForm({ ...form, custom_rate: e.target.value.replace(/[^\d]/g, '') })}
                        placeholder="e.g. 800"
                        data-testid="custom-rate-input"
                        className="w-full text-sm rounded-l-none"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] text-neutral-500 uppercase tracking-wider mb-1.5 font-body">Reason / Label</label>
                    <input
                      value={form.rate_label}
                      onChange={e => setForm({ ...form, rate_label: e.target.value.slice(0, 40) })}
                      placeholder="VIP Block · Tournament · Discount"
                      data-testid="rate-label-input"
                      className="w-full text-sm"
                    />
                  </div>
                </div>

                {/* Quick presets */}
                <div className="flex flex-wrap gap-1.5">
                  <span className="text-[10px] text-neutral-600 uppercase tracking-wider font-body mr-1 self-center">Quick:</span>
                  {[
                    { label: 'VIP Block', rate: 800 },
                    { label: 'Tournament', rate: 600 },
                    { label: 'School Discount', rate: 250 },
                    { label: 'Off-peak Deal', rate: 200 },
                  ].map(p => (
                    <button
                      key={p.label}
                      type="button"
                      onClick={() => setForm({ ...form, custom_rate: String(p.rate), rate_label: p.label })}
                      data-testid={`preset-${p.label.replace(/\s+/g, '-').toLowerCase()}`}
                      className="text-[10px] bg-[#171717] border border-[#1A1A1A] hover:border-[#16A34A]/40 hover:text-[#16A34A] text-neutral-400 px-2 py-1 rounded-md transition-colors"
                    >
                      {p.label} <span className="text-neutral-600">₹{p.rate}</span>
                    </button>
                  ))}
                </div>

                {Number(form.custom_rate) > 0 && (
                  <div className="grid grid-cols-3 gap-2 pt-2">
                    <SummaryPill label="Hours" value={form.is_custom_time ? `${customDuration}` : `${form.time_slots.length}`} />
                    <SummaryPill label="Rate" value={`₹${Number(form.custom_rate)}/hr`} accent="green" />
                    <SummaryPill label="New Total" value={fmtAmount(total)} accent="green" />
                  </div>
                )}
                {Number(form.custom_rate) > 0 && total !== autoTotal && (
                  <div className="text-[11px] text-neutral-500 font-body">
                    {total > autoTotal ? <span className="text-yellow-400">+{fmtAmount(total - autoTotal)} above</span> : <span className="text-[#16A34A]">−{fmtAmount(autoTotal - total)} below</span>} auto rate <span className="text-neutral-700 line-through">{fmtAmount(autoTotal)}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Payment Status */}
          <div className="bg-[#111] border border-[#1A1A1A] rounded-xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <ShieldCheck className="h-4 w-4 text-[#16A34A]" />
              <span className="text-[10px] text-neutral-500 uppercase tracking-wider font-body">Payment Status</span>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <button
                type="button"
                onClick={() => setForm({ ...form, payment_mode: 'fully_paid', amount_paid: '' })}
                data-testid="payment-mode-fully"
                className={`py-3 rounded-lg text-[13px] font-semibold border transition-all ${form.payment_mode === 'fully_paid' ? 'bg-[#16A34A]/10 border-[#16A34A]/40 text-[#16A34A]' : 'bg-[#171717] border-[#1A1A1A] text-neutral-400 hover:border-[#262626]'}`}>
                Fully Paid
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, payment_mode: 'partially_paid' })}
                data-testid="payment-mode-partial"
                className={`py-3 rounded-lg text-[13px] font-semibold border transition-all ${form.payment_mode === 'partially_paid' ? 'bg-yellow-500/10 border-yellow-500/40 text-yellow-400' : 'bg-[#171717] border-[#1A1A1A] text-neutral-400 hover:border-[#262626]'}`}>
                Partially Paid
              </button>
            </div>

            {form.payment_mode === 'partially_paid' && (
              <div className="space-y-3 mt-2">
                <div>
                  <label className="block text-[11px] text-neutral-400 uppercase tracking-wider mb-1.5 font-body">Amount paid (₹)</label>
                  <div className="flex">
                    <div className="flex items-center px-3 bg-[#1A1A1A] border border-r-0 border-[#262626] text-sm text-neutral-400 font-body rounded-l-lg">₹</div>
                    <input
                      type="number"
                      min="1"
                      max={total - 1}
                      step="1"
                      value={form.amount_paid}
                      onChange={e => setForm({ ...form, amount_paid: e.target.value.replace(/[^\d]/g, '') })}
                      placeholder={`Max ₹${total - 1}`}
                      data-testid="amount-paid-input"
                      className="w-full text-sm rounded-l-none"
                    />
                  </div>
                </div>
                {Number(form.amount_paid) > 0 && Number(form.amount_paid) < total && (
                  <div className="grid grid-cols-3 gap-2 pt-1">
                    <SummaryPill label="Total" value={fmtAmount(total)} />
                    <SummaryPill label="Paid" value={fmtAmount(Number(form.amount_paid))} accent="green" />
                    <SummaryPill label="Due" value={fmtAmount(total - Number(form.amount_paid))} accent="yellow" />
                  </div>
                )}
                {form.amount_paid && (Number(form.amount_paid) <= 0 || Number(form.amount_paid) >= total) && (
                  <p className="text-[11px] text-yellow-500/80 font-body">Enter an amount between ₹1 and ₹{total - 1}. For full payment, choose <span className="font-semibold">Fully Paid</span>.</p>
                )}
              </div>
            )}

            {form.payment_mode === 'fully_paid' && (
              <div className="text-[12px] text-neutral-500 font-body">Customer has paid the full amount of <span className="text-[#16A34A] font-semibold">{fmtAmount(total)}</span>.</div>
            )}
          </div>

          <div className="flex justify-between gap-2">
            <button onClick={() => setStep(2)} className="btn-secondary inline-flex items-center gap-2" data-testid="offline-back-3">
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            <button onClick={handleSubmit} disabled={submitting || !canStep3} data-testid="offline-submit"
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {submitting ? 'Creating...' : 'Confirm & Send SMS'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function FieldLabel({ icon: Icon, label, children }) {
  return (
    <div>
      <label className="flex items-center gap-1.5 text-[11px] text-neutral-400 uppercase tracking-wider mb-1.5 font-body">
        {Icon && <Icon className="h-3 w-3" />}{label}
      </label>
      {children}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="text-neutral-500 font-body text-[12px]">{label}</span>
      <span className="text-right text-[13px] truncate min-w-0">{value || '-'}</span>
    </div>
  );
}

/* ─── BLOCK SLOTS PAGE (admin-only, no customer / no SMS) ─── */
function BlockSlotsPage() {
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [selected, setSelected] = useState([]);
  const [reason, setReason] = useState('');
  const [bookedSlots, setBookedSlots] = useState([]);
  const [existingBlocks, setExistingBlocks] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [allBlocks, setAllBlocks] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadDay = useCallback(async (d) => {
    setLoading(true);
    try {
      const [availRes, blockRes] = await Promise.all([
        axios.get(`${API_URL}/api/turfs/turf_cricket01/availability?date=${d}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/block-slots?date=${d}`, { withCredentials: true }),
      ]);
      const allBooked = new Set(availRes.data.booked_slots || []);
      const blockedSet = new Set();
      blockRes.data.forEach(b => (b.time_slots || []).forEach(s => blockedSet.add(s)));
      // Booked-only = unavailable due to confirmed bookings, not just blocks
      const bookedOnly = [...allBooked].filter(s => !blockedSet.has(s));
      setBookedSlots(bookedOnly);
      setExistingBlocks(blockRes.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  const loadAllBlocks = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/block-slots`, { withCredentials: true });
      setAllBlocks(r.data || []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { if (date) loadDay(date); }, [date, loadDay]);
  useEffect(() => { loadAllBlocks(); }, [loadAllBlocks]);

  const toggleSlot = (s) => {
    if (bookedSlots.includes(s)) return;
    setSelected(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  };

  const blockNow = async () => {
    if (selected.length === 0) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/block-slots`,
        { date, time_slots: selected, reason },
        { withCredentials: true }
      );
      setSelected([]);
      setReason('');
      await loadDay(date);
      await loadAllBlocks();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to block slots');
    }
    setSubmitting(false);
  };

  const unblock = async (block_id) => {
    if (!window.confirm('Unblock these slots? They will become available for online booking again.')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/block-slots/${block_id}`, { withCredentials: true });
      await loadDay(date);
      await loadAllBlocks();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to unblock');
    }
  };

  const blockedSetForDate = new Set();
  existingBlocks.forEach(b => (b.time_slots || []).forEach(s => blockedSetForDate.add(s)));

  return (
    <div className="p-6 md:p-8 max-w-4xl" data-testid="admin-block">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <Ban className="h-5 w-5 text-red-400" />
          <h1 className="text-2xl font-bold font-heading">Block Slots</h1>
        </div>
      </div>

      {/* Pick date */}
      <div className="bg-[#111] border border-[#1A1A1A] rounded-xl p-5 mb-4">
        <label className="block text-[10px] text-neutral-500 uppercase tracking-wider mb-2 font-body">Select Date</label>
        <div className="max-w-xs">
          <TurfDatePicker
            value={date}
            onChange={(d) => { setDate(d); setSelected([]); }}
            maxDate={addDays(new Date(), 60)}
            testId="block-date-picker"
          />
        </div>
      </div>

      {/* Slot grid */}
      <div className="bg-[#111] border border-[#1A1A1A] rounded-xl p-5 mb-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <span className="text-[10px] text-neutral-500 uppercase tracking-wider font-body">Slots</span>
          <div className="flex items-center gap-2 text-[10px] font-body">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#171717] border border-[#1A1A1A]"></span><span className="text-neutral-500">Free</span></span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-red-500/15 border border-red-500/30"></span><span className="text-neutral-500">Blocked</span></span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#16A34A]/15 border border-[#16A34A]/30"></span><span className="text-neutral-500">Booked</span></span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-red-500"></span><span className="text-neutral-500">Selected</span></span>
          </div>
        </div>
        {loading ? <div className="py-6 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-[#16A34A]" /></div> : (
          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-1.5">
            {ALL_SLOTS.map(s => {
              const isBooked = bookedSlots.includes(s);
              const isBlocked = blockedSetForDate.has(s);
              const isSel = selected.includes(s);
              let cls = 'py-2 rounded-lg text-[11px] text-center transition-all border ';
              if (isBooked) cls += 'bg-[#16A34A]/15 border-[#16A34A]/30 text-[#16A34A] cursor-not-allowed opacity-70';
              else if (isBlocked) cls += 'bg-red-500/15 border-red-500/30 text-red-400 cursor-not-allowed line-through';
              else if (isSel) cls += 'bg-red-500 border-red-500 text-white font-semibold scale-105';
              else cls += 'bg-[#171717] border-[#1A1A1A] text-neutral-400 hover:border-red-500/40 hover:text-red-400';
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleSlot(s)}
                  disabled={isBooked || isBlocked}
                  data-testid={`block-slot-${s}`}
                  className={cls}
                  title={isBooked ? 'Confirmed booking' : isBlocked ? 'Already blocked' : 'Click to block'}
                >
                  {fmtSlot(s)}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Action bar */}
      <div className="bg-[#111] border border-[#1A1A1A] rounded-xl p-5 mb-6">
        <label className="block text-[10px] text-neutral-500 uppercase tracking-wider mb-2 font-body">Reason (optional)</label>
        <input
          value={reason}
          onChange={e => setReason(e.target.value.slice(0, 200))}
          placeholder="e.g. Ground maintenance · Tournament · Personal use"
          data-testid="block-reason"
          className="w-full text-sm mb-3"
        />
        <button
          onClick={blockNow}
          disabled={submitting || selected.length === 0}
          data-testid="block-submit"
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-red-500 hover:bg-red-600 disabled:opacity-30 disabled:cursor-not-allowed text-white text-[13px] font-semibold rounded-lg transition-colors"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4" />}
          Block {selected.length > 0 ? `${selected.length} Slot${selected.length !== 1 ? 's' : ''}` : 'Slots'}
        </button>
      </div>

      {/* Existing blocks */}
      <div className="bg-[#111] border border-[#1A1A1A] rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[10px] text-neutral-500 uppercase tracking-wider font-body">All Active Blocks</span>
          <button onClick={loadAllBlocks} className="text-[10px] text-neutral-500 hover:text-white" data-testid="refresh-blocks"><RefreshCw className="h-3 w-3 inline-block mr-1" />Refresh</button>
        </div>
        {allBlocks.length === 0 ? (
          <div className="py-8 text-center text-[12px] text-neutral-600 font-body">No blocked slots</div>
        ) : (
          <div className="space-y-2">
            {allBlocks.map(b => (
              <div key={b.block_id} className="flex items-center justify-between gap-3 bg-[#0F0F0F] border border-[#1A1A1A] rounded-lg p-3" data-testid={`block-row-${b.block_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[13px] font-semibold">{b.date}</span>
                    <span className="text-[10px] text-neutral-600">·</span>
                    <span className="text-[11px] text-red-400">{b.time_slots.length} slot{b.time_slots.length !== 1 ? 's' : ''}</span>
                    {b.reason && <span className="text-[10px] text-neutral-500 font-body truncate">· {b.reason}</span>}
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {b.time_slots.map(s => (
                      <span key={s} className="px-1.5 py-0.5 rounded text-[10px] bg-red-500/10 border border-red-500/20 text-red-400 font-medium">{fmtSlot(s)}</span>
                    ))}
                  </div>
                </div>
                <button
                  onClick={() => unblock(b.block_id)}
                  data-testid={`unblock-${b.block_id}`}
                  className="flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-semibold border border-[#1A1A1A] hover:border-red-500/40 text-neutral-400 hover:text-red-400 transition-colors"
                >
                  <Trash2 className="h-3 w-3" /> Unblock
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryPill({ label, value, accent }) {
  const cls = accent === 'green'
    ? 'bg-[#16A34A]/10 border-[#16A34A]/30 text-[#16A34A]'
    : accent === 'yellow'
    ? 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400'
    : 'bg-[#171717] border-[#1A1A1A] text-neutral-300';
  return (
    <div className={`border ${cls} rounded-lg px-3 py-2 text-center`}>
      <div className="text-[9px] uppercase tracking-wider opacity-75 font-body">{label}</div>
      <div className="text-[13px] font-bold mt-0.5" style={{ fontFamily: 'Inter, sans-serif' }}>{value}</div>
    </div>
  );
}

/* ─── HEALTH PAGE ─── */
function HealthPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastFetch, setLastFetch] = useState(null);

  const fetchHealth = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/health`, { withCredentials: true });
      setData(r.data);
      setLastFetch(new Date());
    } catch (e) {
      console.error(e);
      setData({ status: 'down', error: e.message });
    }
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    fetchHealth();
    const t = setInterval(() => fetchHealth(true), 30000);
    return () => clearInterval(t);
  }, [fetchHealth]);

  if (loading) return <div className="p-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[#16A34A]" /></div>;
  if (!data) return <div className="p-8 text-center text-neutral-500">Failed to load health data</div>;

  const fmtUptime = (sec) => {
    if (!sec) return '-';
    const d = Math.floor(sec / 86400);
    const h = Math.floor((sec % 86400) / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    if (d > 0) return `${d}d ${h}h ${m}m`;
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  const overallStatus = data.status; // healthy | degraded | down
  const statusColor = overallStatus === 'healthy' ? 'text-[#16A34A] bg-[#16A34A]/10 border-[#16A34A]/30' : overallStatus === 'degraded' ? 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30' : 'text-red-400 bg-red-400/10 border-red-400/30';
  const StatusIcon = overallStatus === 'healthy' ? CheckCircle : overallStatus === 'degraded' ? AlertTriangle : XCircle;

  return (
    <div className="p-6 md:p-8 max-w-6xl" data-testid="admin-health">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold font-heading">System Health</h1>
          <p className="text-[12px] text-neutral-500 font-body mt-0.5">
            Real-time status of services & integrations
            {lastFetch && <span className="ml-2 text-neutral-700">· Last checked {lastFetch.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata' })}</span>}
          </p>
        </div>
        <button
          onClick={() => fetchHealth(true)}
          disabled={refreshing}
          data-testid="health-refresh"
          className="h-10 inline-flex items-center gap-2 px-4 bg-[#171717] border border-[#1A1A1A] rounded-lg hover:border-[#16A34A]/40 hover:text-[#16A34A] text-neutral-300 text-[13px] font-medium transition-colors disabled:opacity-50"
        >
          {refreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Refresh
        </button>
      </div>

      {/* Overall Status Banner */}
      <div className={`border rounded-xl p-5 mb-6 ${statusColor}`}>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <StatusIcon className="h-6 w-6" />
            <div>
              <div className="text-[11px] uppercase tracking-wider font-body opacity-70">Overall Status</div>
              <div className="text-xl font-bold font-heading capitalize">{overallStatus}</div>
            </div>
          </div>
          <div className="flex gap-6">
            <div>
              <div className="text-[10px] uppercase tracking-wider font-body opacity-70 mb-0.5">Uptime</div>
              <div className="text-lg font-bold font-mono tabular-nums">{fmtUptime(data.uptime_seconds)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-body opacity-70 mb-0.5">Started</div>
              <div className="text-[13px] font-mono">{data.started_at ? new Date(data.started_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'short', timeStyle: 'short' }) : '-'}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Service Status */}
      <h3 className="text-sm font-semibold font-heading mb-3 flex items-center gap-2"><Server className="h-4 w-4 text-[#16A34A]" />Services</h3>
      <div className="grid md:grid-cols-2 gap-3 mb-6">
        <ServiceCard
          icon={Server}
          label="Backend API"
          status={data.services?.backend?.status}
          detail={`v${data.services?.backend?.version || '1.0.0'}`}
          metric="FastAPI"
        />
        <ServiceCard
          icon={Database}
          label="Database"
          status={data.services?.database?.status}
          detail={data.services?.database?.type || 'MongoDB'}
          metric={data.services?.database?.latency_ms != null ? `${data.services.database.latency_ms}ms ping` : null}
        />
      </div>

      {/* Counts */}
      <h3 className="text-sm font-semibold font-heading mb-3 flex items-center gap-2"><Activity className="h-4 w-4 text-[#16A34A]" />Database Records</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <StatCard label="Users" value={data.counts?.users ?? 0} />
        <StatCard label="Bookings" value={data.counts?.bookings ?? 0} color="text-[#16A34A]" />
        <StatCard label="Confirmed" value={data.counts?.confirmed_bookings ?? 0} color="text-[#16A34A]" />
        <StatCard label="Pending" value={data.counts?.pending_bookings ?? 0} color="text-yellow-500" />
        <StatCard label="Contact Forms" value={data.counts?.contact_submissions ?? 0} />
        <StatCard label="Sessions" value={data.counts?.active_sessions ?? 0} />
      </div>

      {/* Integrations */}
      <h3 className="text-sm font-semibold font-heading mb-3 flex items-center gap-2"><Wifi className="h-4 w-4 text-[#16A34A]" />Integrations</h3>
      <div className="grid md:grid-cols-2 gap-3 mb-6">
        <IntegrationCard
          name="Cashfree Payments"
          configured={data.integrations?.cashfree?.configured}
          detail={data.integrations?.cashfree?.environment || 'Not set'}
        />
        <IntegrationCard
          name="Fast2SMS (DLT SMS)"
          configured={data.integrations?.fast2sms?.configured}
          detail={data.integrations?.fast2sms?.configured ? 'API key set' : 'API key missing'}
        />
        <IntegrationCard
          name="Resend (Email)"
          configured={data.integrations?.resend?.configured}
          detail={data.integrations?.resend?.configured ? 'API key set' : 'API key missing'}
        />
        <IntegrationCard
          name="Google OAuth"
          configured={data.integrations?.google_oauth?.configured}
          detail={data.integrations?.google_oauth?.provider || 'Not set'}
        />
      </div>

      {/* Last Booking */}
      <h3 className="text-sm font-semibold font-heading mb-3 flex items-center gap-2"><CalendarCheck className="h-4 w-4 text-[#16A34A]" />Recent Activity</h3>
      <div className="bg-[#111] border border-[#1A1A1A] rounded-xl p-5">
        {data.last_booking ? (
          <div className="grid md:grid-cols-4 gap-4">
            <div>
              <div className="text-[10px] text-neutral-500 uppercase tracking-wider font-body mb-1">Last Booking ID</div>
              <div className="text-[12px] font-mono">{data.last_booking.booking_id}</div>
            </div>
            <div>
              <div className="text-[10px] text-neutral-500 uppercase tracking-wider font-body mb-1">Customer</div>
              <div className="text-[13px]">{data.last_booking.customer_name || '-'}</div>
            </div>
            <div>
              <div className="text-[10px] text-neutral-500 uppercase tracking-wider font-body mb-1">Status</div>
              <div className="text-[13px] capitalize">{data.last_booking.status}</div>
            </div>
            <div>
              <div className="text-[10px] text-neutral-500 uppercase tracking-wider font-body mb-1">Created</div>
              <div className="text-[12px] font-mono">{data.last_booking.created_at ? new Date(data.last_booking.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'short', timeStyle: 'short' }) : '-'}</div>
            </div>
          </div>
        ) : (
          <div className="text-center py-3">
            <CalendarCheck className="h-6 w-6 text-neutral-700 mx-auto mb-2" />
            <p className="text-[12px] text-neutral-500 font-body">No bookings yet</p>
          </div>
        )}
      </div>

      <div className="mt-6 flex items-center justify-between text-[10px] text-neutral-600 font-body">
        <span>Auto-refreshes every 30 seconds</span>
        <span>Timestamp: {new Date(data.timestamp).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} IST</span>
      </div>
    </div>
  );
}

function ServiceCard({ icon: Icon, label, status, detail, metric }) {
  const isUp = status === 'up';
  const dotColor = isUp ? 'bg-[#16A34A]' : 'bg-red-500';
  const statusText = isUp ? 'Operational' : 'Down';
  const statusColor = isUp ? 'text-[#16A34A]' : 'text-red-400';

  return (
    <div className="bg-[#111] border border-[#1A1A1A] rounded-xl p-4 flex items-center gap-3 hover:border-[#262626] transition-colors" data-testid={`service-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="w-10 h-10 rounded-lg bg-[#16A34A]/10 border border-[#16A34A]/20 flex items-center justify-center flex-shrink-0">
        <Icon className="h-4 w-4 text-[#16A34A]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-sm font-semibold font-heading truncate">{label}</span>
          <span className={`w-1.5 h-1.5 rounded-full ${dotColor} flex-shrink-0`}></span>
        </div>
        <div className="flex items-center gap-2 text-[11px]">
          <span className={`font-semibold ${statusColor}`}>{statusText}</span>
          <span className="text-neutral-700">·</span>
          <span className="text-neutral-500 font-body">{detail}</span>
          {metric && <><span className="text-neutral-700">·</span><span className="text-neutral-400 font-mono">{metric}</span></>}
        </div>
      </div>
    </div>
  );
}

function IntegrationCard({ name, configured, detail }) {
  return (
    <div className="bg-[#111] border border-[#1A1A1A] rounded-xl p-4 hover:border-[#262626] transition-colors" data-testid={`integration-${name.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-semibold font-heading">{name}</span>
        {configured ? (
          <span className="text-[9px] bg-[#16A34A]/10 text-[#16A34A] border border-[#16A34A]/20 px-2 py-0.5 rounded font-bold uppercase tracking-wider">Connected</span>
        ) : (
          <span className="text-[9px] bg-red-500/10 text-red-400 border border-red-500/20 px-2 py-0.5 rounded font-bold uppercase tracking-wider">Missing</span>
        )}
      </div>
      <div className="text-[11px] text-neutral-500 font-body">{detail}</div>
    </div>
  );
}

export default AdminPage;
